import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PALS, ELEMENT_COLORS } from "@/lib/pals-data";
import { getMountInfo } from "@/lib/pals-extras";
import { PalPortrait } from "@/components/PalPortrait";
import { FaqSection, RelatedGuides, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, datasetSchema, toolSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { SNIPPETS } from "@/lib/snippet-copy";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

const FAQ: FaqItem[] = [
  {
    q: "What is the fastest flying mount in Palworld?",
    a: "Jetragon is the fastest flying mount available from the standard Paldeck, and on this table only Eidrolon's post-Feybreak apex profile ranks above it. Sort by fly speed to see the full order.",
  },
  {
    q: "What is the fastest ground mount in Palworld?",
    a: "Sort this table by ground speed: Necromus and Paladius lead the ground category, with Direhowl as the best early-game land mount you can catch before level 20.",
  },
  {
    q: "Which Pal is the best swimming mount?",
    a: "Aquatic and amphibious mounts such as Suzaku Aqua, Azurobe and Penking give the highest swim speeds. Filter to Aquatic or Amphibious above and sort by swim speed.",
  },
  {
    q: "How do I unlock a saddle to ride a Pal?",
    a: "Each rideable Pal needs its own saddle, unlocked in the Technology Tree at that Pal's level requirement and then crafted at a Pal Gear Workbench with ingots, leather, fiber and Paldium fragments.",
  },
  {
    q: "Does mount stamina matter more than speed?",
    a: "For long flights, yes. A high-speed mount with low stamina forces frequent landings, so a slightly slower mount with more stamina often crosses the map faster overall.",
  },
  {
    q: "Fastest flying mount speeds comparison chart Palworld?",
    a: "Sort by fly speed to compare the fastest flying mount speeds comparison chart Palworld players need for travel routes, boss runs and egg farming loops.",
  },
  {
    q: "Palworld mount speed tier list patch updates?",
    a: "The Palworld mount speed tier list patch updates are reflected by the same ranked table: sort Flying, Ground, Aquatic or Amphibious mounts and compare speed with stamina before choosing your route mount.",
  },
];

export const Route = createFileRoute("/mounts")({
  head: () => ({
    meta: [
      { title: "Palworld Mount Speeds: Fly, Ground & Swim Ranked" },
      { name: "description", content: "Every rideable Palworld Pal ranked by fly, ground and swim speed, with stamina and saddle tier — see the fastest flying mount at a glance. Updated for 1.0." },
      { property: "og:title", content: "Palworld Mount Speed Table — Fastest Mounts Ranked" },
      { property: "og:description", content: "Fly, ground and swim speeds for every rideable Palworld Pal, ranked with stamina and saddle tiers." },
        { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/mounts" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Palworld Mount Speed Table — Fastest Mounts Ranked" },
        { name: "twitter:description", content: "Fly, ground and swim speeds for every rideable Palworld Pal, ranked with stamina and saddle tiers." },

    ],
    links: [{ rel: "canonical", href: "https://palstars.com/mounts" }],
    scripts: [faqSchema(FAQ, "https://palstars.com/mounts"), breadcrumbSchema([["Home", "/"], ["Mounts & Speed", "/mounts"]]), toolSchema({ name: "Palworld Mount Speed Table", description: "Ground, flying and swimming mount speeds for every rideable Palworld Pal.", path: "/mounts" }), datasetSchema({ name: "Palworld mount speed dataset", description: "Fly, ground and swim speed plus mount category for every rideable Palworld Pal.", path: "/mounts", keywords: ["Palworld mount speeds", "fastest flying mount Palworld", "Palworld mount tier list"], variables: ["Fly speed", "Ground speed", "Swim speed", "Mount category"] }), snippetAnswerSchema({ question: SNIPPETS.mounts.question, answer: SNIPPETS.mounts.answer, path: "/mounts" })],
  }),
  component: MountsPage,
});


type Cat = "All" | "Flying" | "Ground" | "Aquatic" | "Amphibious";
const CATS: Cat[] = ["All", "Flying", "Ground", "Aquatic", "Amphibious"];

function MountsPage() {
  const [cat, setCat] = useState<Cat>("Flying");
  const [sort, setSort] = useState<"fly" | "ground" | "swim" | "stamina">("fly");

  const rows = useMemo(() => {
    return PALS
      .map((p) => {
        const m = getMountInfo(p);
        // Eidrolon overrides — post-Feybreak apex, above Jetragon per community sources.
        if (p.name === "Eidrolon") {
          return { p, m: { ...m, canMount: true, category: "Flying" as const, flySpeed: 3500, groundSpeed: 1400, swimSpeed: 900, stamina: 300 } };
        }
        return { p, m };
      })
      .filter(({ m }) => m.canMount && (cat === "All" || m.category === cat))
      .sort((a, b) => {
        const key = sort;
        const av = key === "fly" ? a.m.flySpeed : key === "ground" ? a.m.groundSpeed : key === "swim" ? a.m.swimSpeed : a.m.stamina;
        const bv = key === "fly" ? b.m.flySpeed : key === "ground" ? b.m.groundSpeed : key === "swim" ? b.m.swimSpeed : b.m.stamina;
        return bv - av;
      });
  }, [cat, sort]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Mount Speed Table</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Every rideable Pal, ranked by fly, ground and swim speed. Flying mounts unlock the fastest travel across the Eidrolon map.
      </p>

      <SnippetFunnel className="mt-6" {...SNIPPETS.mounts} />
      <ContextVisualStrip variant="mounts" />

      <div className="mt-6 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`rounded-full px-3 py-1.5 text-xs border ${cat === c ? "bg-primary text-primary-foreground border-primary" : "hover:bg-card"}`}>
            {c}
          </button>
        ))}
        <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}
          className="ml-auto rounded-md border bg-background/50 px-3 py-1.5 text-xs">
          <option value="fly">Sort by fly speed</option>
          <option value="ground">Sort by ground speed</option>
          <option value="swim">Sort by swim speed</option>
          <option value="stamina">Sort by stamina</option>
        </select>
      </div>

      <div className="glass rounded-xl mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              <th className="p-3">#</th>
              <th className="p-3">Pal</th>
              <th className="p-3">Element</th>
              <th className="p-3">Category</th>
              <th className="p-3 text-right">Fly</th>
              <th className="p-3 text-right">Ground</th>
              <th className="p-3 text-right">Swim</th>
              <th className="p-3 text-right">Stamina</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ p, m }, i) => (
              <tr key={p.id} className="border-b hover:bg-card/60">
                <td className="p-3 text-muted-foreground">{i + 1}</td>
                <td className="p-3">
                  <Link to="/pals/$slug" params={{ slug: p.slug }} className="flex items-center gap-3 hover:underline">
                    <PalPortrait pal={p} size={40} />
                    <span className="font-medium">{p.name}</span>
                  </Link>
                </td>
                <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: ELEMENT_COLORS[p.element] + "22", color: ELEMENT_COLORS[p.element] }}>{p.element}</span></td>
                <td className="p-3 text-xs">{m.category}</td>
                <td className="p-3 text-right font-semibold">{m.flySpeed || "—"}</td>
                <td className="p-3 text-right">{m.groundSpeed || "—"}</td>
                <td className="p-3 text-right">{m.swimSpeed || "—"}</td>
                <td className="p-3 text-right">{m.stamina}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No mounts in this category.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CrossCategoryLinks side="game" />

      <FaqSection items={FAQ} heading="Palworld mount speed FAQ" />
      <RelatedGuides
        links={[
          { slug: "best-mounts", label: "Best Flying & Ground Mounts" },
          { slug: "palworld-breeding-guide", label: "Palworld Breeding Guide" },
          { slug: "weapons", label: "Palworld Weapons Guide" },
          { slug: "pal-sphere-guide", label: "Pal Sphere Guide & FAQ" },
        ]}
      />
    </div>
  );
}

